import { BoardCamera } from '@ue-too/board';
import type { Point } from '@ue-too/math';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { StationManager } from 'track-layout';

import { ArrowLeftRight, Crosshair, Focus, Trash2 } from '@/assets/icons';
import { DraggablePanel } from '@/components/ui/draggable-panel';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
    type CarCargoStore,
    RESOURCE_TYPES,
    type TransferManager,
} from '@/resources';
import type { ThrottleSteps } from '@/trains/formation';
import type { StationPresenceDetector } from '@/trains/station-presence-detector';
import type { TrainManager } from '@/trains/train-manager';
import type { FocusAnimationParams } from '@/utils/init-app';

import { ThrottleIndicator } from './ThrottleIndicator';
import { ToolbarButton } from './ToolbarButton';

type TrainPanelProps = {
    trainManager: TrainManager;
    onClose: () => void;
    startFocusAnimation: (params: FocusAnimationParams) => void;
    startFollowAnimation: (
        params: FocusAnimationParams,
        getPosition: () => Point | null
    ) => void;
    stopFollowing: () => void;
    isFollowing: () => boolean;
    camera: BoardCamera;
    stationPresenceDetector: StationPresenceDetector;
    stationManager: StationManager;
    carCargoStore: CarCargoStore;
    transferManager: TransferManager;
};

type TrainCargoSectionProps = {
    trainId: number;
    carIds: readonly string[];
    carCargoStore: CarCargoStore;
    transferManager: TransferManager;
};

function TrainCargoSection({
    trainId,
    carIds,
    carCargoStore,
    transferManager,
}: TrainCargoSectionProps) {
    const { t } = useTranslation();
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick(x => x + 1), 250);
        return () => clearInterval(id);
    }, []);

    const transfer = transferManager.getTransfer(trainId);

    return (
        <section className="flex flex-col gap-2 p-2">
            <h4 className="text-sm font-semibold">
                {t('panel.resources.title')}
            </h4>
            {transfer && (
                <p className="text-muted-foreground text-xs">
                    {t('panel.resources.transferring')}
                </p>
            )}
            <ul className="flex flex-col gap-2">
                {carIds.map((carId, i) => {
                    const cargo = carCargoStore.getCargo(carId);
                    const total = carCargoStore.getTotalLoad(carId);
                    return (
                        <li
                            key={carId}
                            className="flex flex-col gap-0.5 text-sm"
                        >
                            <header className="flex justify-between">
                                <span>#{i + 1}</span>
                                <span className="tabular-nums">
                                    {Math.floor(total)} / {cargo.capacity}
                                </span>
                            </header>
                            {total === 0 ? (
                                <em className="text-muted-foreground text-xs">
                                    {t('panel.resources.empty')}
                                </em>
                            ) : (
                                <ul className="flex flex-col gap-0.5 pl-3">
                                    {RESOURCE_TYPES.filter(
                                        rt => (cargo.contents[rt.id] ?? 0) > 0
                                    ).map(rt => (
                                        <li
                                            key={rt.id}
                                            className="flex justify-between text-xs"
                                        >
                                            <span>{t(rt.displayNameKey)}</span>
                                            <span className="tabular-nums">
                                                {Math.floor(
                                                    cargo.contents[rt.id] ?? 0
                                                )}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

export function TrainPanel({
    trainManager,
    startFocusAnimation,
    startFollowAnimation,
    stopFollowing,
    isFollowing,
    onClose,
    camera,
    stationPresenceDetector,
    stationManager,
    carCargoStore,
    transferManager,
}: TrainPanelProps) {
    const { t } = useTranslation();
    const [following, setFollowing] = useState(isFollowing());
    const placedTrains = trainManager.getPlacedTrains();
    const selectedTrain = trainManager.getSelectedTrain();
    const selectedIndex = trainManager.selectedIndex;

    // Poll train throttle/speed/station at animation-frame rate
    const [throttle, setThrottle] = useState<ThrottleSteps>('N');
    const [speed, setSpeed] = useState(0);
    const [stationName, setStationName] = useState<string | null>(null);
    const rafRef = useRef<number>(0);

    useEffect(() => {
        if (!selectedTrain) return;
        const tick = () => {
            setThrottle(selectedTrain.throttleStep);
            setSpeed(selectedTrain.speed);
            const presence =
                stationPresenceDetector.getPresenceForTrain(selectedIndex);
            if (presence) {
                const station = stationManager.getStation(presence.stationId);
                setStationName(station?.name ?? null);
            } else {
                setStationName(null);
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [selectedTrain, selectedIndex, stationPresenceDetector, stationManager]);

    const handleThrottleChange = useCallback(
        (step: ThrottleSteps) => {
            selectedTrain?.setThrottleStep(step);
        },
        [selectedTrain]
    );

    return (
        <DraggablePanel title={t('trains')} onClose={onClose} className="w-52">
            <Separator className="mb-2" />
            <div className="flex flex-col gap-2">
                {/* Train list */}
                {placedTrains.length > 0 && (
                    <div className="flex flex-col gap-1">
                        <span className="text-muted-foreground text-xs font-medium">
                            {t('placedTrains')}
                        </span>
                        <div className="flex max-h-32 flex-col gap-0.5 overflow-y-auto">
                            {placedTrains.map((entry, index) => {
                                const formation = entry.train.formation;
                                const carCount = formation.flatCars().length;
                                return (
                                    <button
                                        key={entry.id}
                                        type="button"
                                        onClick={() =>
                                            trainManager.setSelectedIndex(
                                                entry.id
                                            )
                                        }
                                        className={cn(
                                            'flex w-full items-center gap-2 rounded-md px-2 py-1 text-left text-xs transition-colors',
                                            entry.id === selectedIndex
                                                ? 'bg-primary text-primary-foreground'
                                                : 'bg-muted/50 hover:bg-muted'
                                        )}
                                    >
                                        <span className="font-mono font-medium">
                                            {index + 1}
                                        </span>
                                        <span className="truncate">
                                            {formation.name}
                                        </span>
                                        <span
                                            className={cn(
                                                'ml-auto shrink-0 text-[10px]',
                                                entry.id === selectedIndex
                                                    ? 'text-primary-foreground/70'
                                                    : 'text-muted-foreground'
                                            )}
                                        >
                                            {t('car', { count: carCount })}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Selected train: throttle + controls */}
                {selectedTrain && (
                    <>
                        <Separator />
                        <ThrottleIndicator
                            currentStep={throttle}
                            speed={speed}
                            onThrottleChange={handleThrottleChange}
                        />
                        {stationName && (
                            <div className="bg-muted/50 rounded px-2 py-1 text-center text-xs">
                                <span className="text-muted-foreground">
                                    {t('atStation', { name: stationName })}
                                </span>
                            </div>
                        )}
                        <Separator />
                        <div className="flex flex-wrap gap-1">
                            <ToolbarButton
                                tooltip={t('focusOnSelectedTrain')}
                                onClick={() => {
                                    const point = selectedTrain.position?.point;
                                    if (!point) return;
                                    startFocusAnimation({
                                        startWorldPoint: camera.position,
                                        targetWorldPoint: point,
                                        startZoom: camera.zoomLevel,
                                        targetZoom: 5,
                                    });
                                }}
                            >
                                <Focus />
                            </ToolbarButton>
                            <ToolbarButton
                                tooltip={
                                    following
                                        ? t('stopFollowing')
                                        : t('followSelectedTrain')
                                }
                                active={following}
                                onClick={() => {
                                    if (following) {
                                        stopFollowing();
                                        setFollowing(false);
                                        return;
                                    }
                                    const point = selectedTrain.position?.point;
                                    if (!point) return;
                                    startFollowAnimation(
                                        {
                                            startWorldPoint: camera.position,
                                            targetWorldPoint: point,
                                            startZoom: camera.zoomLevel,
                                            targetZoom: 5,
                                        },
                                        () =>
                                            selectedTrain.position?.point ??
                                            null
                                    );
                                    setFollowing(true);
                                }}
                            >
                                <Crosshair />
                            </ToolbarButton>
                            <ToolbarButton
                                tooltip={t('switchDirection')}
                                onClick={() => selectedTrain.switchDirection()}
                            >
                                <ArrowLeftRight />
                            </ToolbarButton>
                            <ToolbarButton
                                tooltip={t('removeSelectedTrain')}
                                destructive
                                onClick={() =>
                                    trainManager.removeSelectedTrain()
                                }
                            >
                                <Trash2 />
                            </ToolbarButton>
                        </div>
                        <Separator />
                        <TrainCargoSection
                            trainId={selectedIndex}
                            carIds={selectedTrain.cars.map(c => c.id)}
                            carCargoStore={carCargoStore}
                            transferManager={transferManager}
                        />
                    </>
                )}

                {/* Controls shown when no train selected but trains exist */}
                {!selectedTrain && placedTrains.length > 0 && (
                    <span className="text-muted-foreground text-center text-xs">
                        {t('placedTrains')}
                    </span>
                )}
            </div>
        </DraggablePanel>
    );
}
